import { useState, useRef, useEffect, useCallback } from "react";
import "@/App.css";
import { AppContext } from "./context";
import { loadState, saveState, syncExceptions } from "./lib/engine";
import { useActions } from "./useActions";
import { Modal } from "./modals/Modal";
import Dashboard from "./screens/Dashboard";
import LoanCases from "./screens/LoanCases";
import Partners from "./screens/Partners";
import Payouts from "./screens/Payouts";
import Reconciliation from "./screens/Reconciliation";
import Exceptions from "./screens/Exceptions";
import CaseDetail from "./modals/CaseDetail";
import NewCase from "./modals/NewCase";
import ImportReceipts from "./modals/ImportReceipts";
import ExceptionEdit from "./modals/ExceptionEdit";
import ResetConfirm from "./modals/ResetConfirm";
import PartnerLimit from "./modals/PartnerLimit";
import { ResetPassword, DeleteUser } from "./modals/UserModals";
import ChangePassword from "./modals/ChangePassword";
import Login from "./screens/Login";
import FirstPassword from "./screens/FirstPassword";
import Users from "./screens/Users";
import { api, ROLE_LABEL } from "./lib/api";

const SCREENS = {
  "Dashboard": Dashboard,
  "Loan Cases": LoanCases,
  "DSA Partners": Partners,
  "Payout Approvals": Payouts,
  "Reconciliation": Reconciliation,
  "Exceptions": Exceptions
};

const MODALS = { case: CaseDetail, new: NewCase, import: ImportReceipts, exception: ExceptionEdit, reset: ResetConfirm, limit: PartnerLimit,
  password: ResetPassword, deleteUser: DeleteUser, changePassword: ChangePassword };

const screensFor = user => (user.role === "admin" ? { ...SCREENS, Users } : SCREENS);

const slug = t => t.toLowerCase().replace(/\s+/g, "-");

const initState = () => {
  const s = loadState();
  syncExceptions(s);
  saveState(s);
  return s;
};

const Toast = ({ message }) => (
  <div className="toast" role="status" aria-live="polite" data-testid="toast">{message}</div>
);

const Sidebar = ({ view, navigate, screens }) => (
  <aside>
    <div className="brand" data-testid="brand">Loan<span>Control</span></div>
    <div className="subtitle">Partner finance operations</div>
    <nav>
      {Object.keys(screens).map(name => (
        <button key={name} data-testid={`nav-${slug(name)}`} className={view === name ? "active" : ""} onClick={() => navigate(name)}>
          {name}
        </button>
      ))}
    </nav>
    <div className="side-note">
      PAN INDIA OPERATIONS<br /><br />Fictional demonstration<br />Browser storage only<br />No bank connections
    </div>
  </aside>
);

const TopBar = ({ user, onReset, onLogout, onChangePassword }) => (
  <header>
    <div>
      <span className="pill warn" data-testid="simulation-badge">SIMULATION ONLY</span>
      <strong> India operations workspace</strong>
    </div>
    <div className="actions">
      <div className="who" data-testid="current-user">
        <strong>{user.name}</strong>
        <small className="muted">{user.email} · <span data-testid="current-user-role">{ROLE_LABEL[user.role]}</span></small>
      </div>
      <button className="danger" data-testid="reset-demo-btn" onClick={onReset}>Reset demo</button>
      <button data-testid="change-password-btn" onClick={onChangePassword}>Change password</button>
      <button data-testid="logout-btn" onClick={onLogout}>Sign out</button>
    </div>
  </header>
);

export default function App() {
  const [state, setState] = useState(initState);
  const [view, setView] = useState("Dashboard");
  const [user, setUser] = useState(null);
  const actor = user ? user.role : "";
  const [search, setSearch] = useState("");
  const [region, setRegion] = useState("All regions");
  const [modal, setModal] = useState(null);
  const [toast, setToast] = useState("");
  const timer = useRef();
  const stateRef = useRef(state);

  const notify = useCallback(message => {
    setToast(message);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 6000);
  }, []);

  const commit = fn => {
    const next = structuredClone(stateRef.current);
    const result = fn(next) || next;
    if (!saveState(result)) notify("Browser storage unavailable. Changes will last only in this tab.");
    stateRef.current = result;
    setState(result);
  };

  const navigate = name => {
    setView(name);
    setSearch("");
    setRegion("All regions");
  };
  const openModal = m => {
    setToast("");
    setModal(m);
  };
  const closeModal = () => setModal(null);
  const actions = useActions({ actor, who: user?.email || "", commit, notify, closeModal, navigate, openModal });

  useEffect(() => {
    document.title = view + " | LoanControl";
  }, [view]);

  useEffect(() => {
    api.get("/auth/me").then(r => setUser(r.data)).catch(() => setUser(false));
  }, []);

  const login = u => {
    setUser(u);
    setModal(null);
    navigate("Dashboard");
  };
  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setModal(null);
    setToast("");
    setUser(false);
  };

  if (user === null) return <div className="empty" data-testid="auth-loading">Checking session…</div>;
  if (user === false) return <Login onLogin={login} />;
  if (user.must_change_password) {
    return (
      <>
        <FirstPassword user={user} onLogout={logout}
          onDone={() => { setUser({ ...user, must_change_password: false }); notify("Password saved. Welcome to LoanControl."); }} />
        {toast && <Toast message={toast} />}
      </>
    );
  }

  const screens = screensFor(user);
  const Screen = screens[view] || Dashboard;
  const ModalBody = modal && MODALS[modal.type];
  const ctx = { state, actor, user, search, setSearch, region, setRegion, navigate, openModal, closeModal, notify, actions };

  return (
    <AppContext.Provider value={ctx}>
      <div className="shell">
        <Sidebar view={view} navigate={navigate} screens={screens} />
        <main>
          <TopBar user={user} onReset={actions.openReset} onLogout={logout} onChangePassword={() => openModal({ type: "changePassword" })} />
          <section className="content" data-testid={`screen-${slug(view)}`}><Screen /></section>
        </main>
      </div>
      {ModalBody && (
        <Modal onClose={closeModal}>
          <ModalBody {...modal} />
          {toast && <Toast message={toast} />}
        </Modal>
      )}
      {!ModalBody && toast && <Toast message={toast} />}
    </AppContext.Provider>
  );
}
